/**
 * Edit meal route
 * Organizer-only page for editing existing meals
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  useMeal,
  useUpdateMeal,
  useAddMealTag,
  useRemoveMealTag,
} from '@/hooks/useMeals';
import { MealForm, type MealFormValues } from '@/components/meals/MealForm';
import { useState, useEffect } from 'react';
import { requireOrganizer } from '@/lib/auth/routeGuards';

export const Route = createFileRoute('/meals/$mealId_/edit')({
  beforeLoad: requireOrganizer,
  component: EditMealComponent,
});

function EditMealComponent() {
  const { mealId } = Route.useParams();
  const navigate = useNavigate();

  const mealIdNum = parseInt(mealId, 10);
  const isValidMealId = Number.isFinite(mealIdNum);
  const { data: meal, isLoading: isMealLoading, error: mealError } = useMeal(
    isValidMealId ? mealIdNum : -1,
  );

  const updateMealMutation = useUpdateMeal();
  const addTagMutation = useAddMealTag();
  const removeTagMutation = useRemoveMealTag();

  const [successMessage, setSuccessMessage] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(false);

  // Track initial tags to compare changes
  const [initialTags, setInitialTags] = useState<Array<{ id: number; tag: string; isHidden: boolean }>>([]);

  useEffect(() => {
    if (meal?.tags) {
      setInitialTags(meal.tags);
    }
  }, [meal]);

  useEffect(() => {
    if (!successMessage) return;
    const timeoutId = setTimeout(() => {
      navigate({ to: `/meals/${mealIdNum}` });
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [successMessage, mealIdNum, navigate]);

  // Invalid meal id in URL (e.g. /meals/abc/edit)
  if (!isValidMealId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate({ to: '/meals' })}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-6"
          >
            <svg
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to meals
          </button>

          <div className="rounded-md bg-red-50 p-4" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Invalid meal id</h3>
                <div className="mt-2 text-sm text-red-700">
                  The meal id in the URL is not a valid number.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while loading meal
  if (isMealLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (mealError || !meal) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate({ to: '/meals' })}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-6"
          >
            <svg
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to meals
          </button>

          <div className="rounded-md bg-red-50 p-4" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading meal</h3>
                <div className="mt-2 text-sm text-red-700">
                  {mealError instanceof Error
                    ? mealError.message
                    : 'Meal not found or failed to load.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (values: MealFormValues) => {
    try {
      setOperationInProgress(true);

      // 1. Update meal planning fields if changed
      if (
        values.name !== meal.name ||
        values.repeatIntervalWeeks !== meal.repeatIntervalWeeks
      ) {
        await updateMealMutation.mutateAsync({
          id: mealIdNum,
          data: {
            name: values.name,
            repeatIntervalWeeks: values.repeatIntervalWeeks,
          },
        });
      }

      // 2. Calculate tag changes
      const currentTagsMap = new Map(initialTags.map((t) => [t.tag.toLowerCase(), t]));
      const newTagsMap = new Map(values.tags.map((t) => [t.tag.toLowerCase(), t]));

      // Find tags to remove
      const tagsToRemove = initialTags.filter((t) => !newTagsMap.has(t.tag.toLowerCase()));

      // Find tags to add
      const tagsToAdd = values.tags.filter((t) => !currentTagsMap.has(t.tag.toLowerCase()));

      // 3. Remove old tags
      for (const tag of tagsToRemove) {
        await removeTagMutation.mutateAsync({ mealId: mealIdNum, tagId: tag.id });
      }

      // 4. Add new tags
      for (const tag of tagsToAdd) {
        await addTagMutation.mutateAsync({
          mealId: mealIdNum,
          data: { tag: tag.tag, isHidden: tag.isHidden },
        });
      }

      // 5. Update visibility for existing tags if changed
      for (const newTag of values.tags) {
        const existingTag = currentTagsMap.get(newTag.tag.toLowerCase());
        if (existingTag && existingTag.isHidden !== newTag.isHidden) {
          // Remove and re-add with new visibility
          await removeTagMutation.mutateAsync({ mealId: mealIdNum, tagId: existingTag.id });
          await addTagMutation.mutateAsync({
            mealId: mealIdNum,
            data: { tag: newTag.tag, isHidden: newTag.isHidden },
          });
        }
      }

      // Show success message; navigation is scheduled by an effect that
      // cleans up its timeout on unmount.
      setSuccessMessage(true);
    } catch (error) {
      console.error('Failed to update meal:', error);
    } finally {
      setOperationInProgress(false);
    }
  };

  const handleCancel = () => {
    navigate({ to: `/meals/${mealIdNum}` });
  };

  const isLoading =
    updateMealMutation.isPending ||
    addTagMutation.isPending ||
    removeTagMutation.isPending ||
    operationInProgress;

  const hasError =
    updateMealMutation.isError || addTagMutation.isError || removeTagMutation.isError;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate({ to: `/meals/${mealIdNum}` })}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-4"
          >
            <svg
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to meal
          </button>

          <h1 className="text-3xl font-bold text-gray-900">Edit Meal</h1>
          <p className="mt-2 text-sm text-gray-600">
            Update meal details and manage tags
          </p>
        </div>

        {/* Success message */}
        {successMessage && (
          <div className="rounded-md bg-green-50 p-4 mb-6" role="alert">
            <div className="flex">
              <svg
                className="h-5 w-5 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Meal updated successfully! Redirecting...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {hasError && (
          <div className="rounded-md bg-red-50 p-4 mb-6" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error updating meal</h3>
                <div className="mt-2 text-sm text-red-700">
                  {updateMealMutation.error instanceof Error
                    ? updateMealMutation.error.message
                    : addTagMutation.error instanceof Error
                    ? addTagMutation.error.message
                    : removeTagMutation.error instanceof Error
                    ? removeTagMutation.error.message
                    : 'Failed to update meal. Please try again.'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Card */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <MealForm
            initialValues={meal}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isLoading}
            submitText="Save Changes"
          />
        </div>
      </div>
    </div>
  );
}
